import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "@/store/store";
import { syncFromURL } from "@/store/slices/userSelection/userSelection";
import type { UserSelectionState } from "@/store/slices/userSelection/userSelection";
import { parseURLParams } from "@/utils/url";
import { buildContextKey } from "@/store/slices/screenLevelSelection/contextKey";
import { updateScreenLevelSelection } from "@/store/slices/screenLevelSelection/screenLevelSelection";
import { selectSupportedURLKeys } from "@/store/slices/screenConfig/selectors";
import { selectAgeFilterCode } from "@/store/slices/userSelection/selectors";
import { setPendingSelection } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import type { FilterSelection } from "@interfaces/filters/domain";
import {
  HUMAN_METRIC,
  MODELS,
} from "@/constant/constantsThrougoutProject";

export const useURLToStore = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const supportedURLKeys = useSelector(selectSupportedURLKeys);
  const ageFilterCode = useSelector(selectAgeFilterCode);

  // Keep a ref so the location-effect can read the latest ageFilterCode
  // without needing it in its dependency array (which would cause the effect
  // to re-run on every catalog load, not just on URL changes).
  const ageFilterCodeRef = useRef(ageFilterCode);
  useEffect(() => {
    ageFilterCodeRef.current = ageFilterCode;
  }, [ageFilterCode]);

  /**
   * Stores a pending age range when the URL has legacy `ageStart`/`ageEnd`
   * params but the filter catalog isn't loaded yet (ageFilterCode === undefined).
   * Cleared once the catalog loads and the retry effect fires.
   */
  const pendingAgeRangeRef = useRef<{
    start: number;
    end: number;
    existingFilters: FilterSelection[];
  } | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // parseURLParams already uses getAll() for:
    //   brands, markets, segments, filters, secondaryBrands → number[]
    //   questionOptions → Record<string, QuestionPickOption[]> (parsed from
    //     legacy "qKey-optId" strings; picks are stamped RESPONSE, never retyped)
    // and get() for all other single-value keys → string
    const parsed: Record<string, any> = parseURLParams(searchParams);

    // Derive module from first pathname segment.
    // Fall back to "VISIBILITY" (not "EQUITY") so that an initial load at "/"
    // does not override the userSelection initial state with the wrong module
    // before Home.tsx has had a chance to redirect to the correct first module.
    //
    // Path aliases (route segment → userSelection.module / registry key):
    //   emotional    → IMAGERY  (then selectionListener remaps to EMOTIONAL)
    //   aivisibility → AI_VISIBILITY
    const pathModule = location.pathname.split("/")?.[1];
    const module =
      pathModule === "emotional"
        ? "IMAGERY"
        : pathModule === "aivisibility"
          ? "AI_VISIBILITY"
          : pathModule?.toUpperCase() || "VISIBILITY";

    const screenType = location.pathname?.split("/")?.[2] || "brand";

    // ── Legacy ageStart / ageEnd → RANGE FilterSelection migration ──────────
    // Old URLs used `ageStart=18&ageEnd=120` params. Convert them to the v2
    // RANGE FilterSelection format (`fv=2&f=<code>:r=18-120`) so that the URL
    // is immediately updated to the new format when a legacy link is opened.
    //
    // Flow when catalog IS loaded (ageFilterCode available):
    //   1. Create RANGE FilterSelection and inject into parsed.filters.
    //   2. syncFromURL commits the selection (including the RANGE filter).
    //   3. setPendingSelection triggers selectionListener → navigateTo with
    //      the new fv=2&f=<code>:r=18-120 URL.
    //
    // Flow when catalog is NOT yet loaded (ageFilterCode undefined):
    //   1. Store { start, end, existingFilters } in pendingAgeRangeRef.
    //   2. syncFromURL commits the rest of the selection (no age filter yet).
    //   3. The retry useEffect below fires once ageFilterCode becomes available
    //      and dispatches setPendingSelection to trigger navigation.
    const legacyAgeStart = searchParams.get("ageStart");
    const legacyAgeEnd = searchParams.get("ageEnd");
    let legacyAgeRangeHandled = false;
    // Reset on every URL commit so a range stored for an earlier URL can never
    // be applied to this one; it is re-armed below only for a valid pair.
    pendingAgeRangeRef.current = null;

    if (legacyAgeStart !== null && legacyAgeEnd !== null) {
      const start = Number(legacyAgeStart);
      const end = Number(legacyAgeEnd);
      if (Number.isFinite(start) && Number.isFinite(end) && start <= end) {
        const existingFilters = Array.isArray(parsed.filters)
          ? (parsed.filters as FilterSelection[])
          : [];
        const currentAgeFilterCode = ageFilterCodeRef.current;
        if (currentAgeFilterCode) {
          // Catalog is loaded — create the RANGE FilterSelection immediately.
          const ageRangeSelection: FilterSelection = {
            code: currentAgeFilterCode,
            kind: "RANGE",
            range: { min: start, max: end },
          };
          parsed.filters = [
            ...existingFilters.filter((f) => f.code !== currentAgeFilterCode),
            ageRangeSelection,
          ];
          legacyAgeRangeHandled = true;
        } else {
          // Catalog not yet loaded — store for retry when ageFilterCode arrives.
          pendingAgeRangeRef.current = { start, end, existingFilters };
        }
      }
    }

    // `parsed.filters` is now a structured `FilterSelection[]` produced by
    // the v2 `fv=2` + `f=` URL grammar (spec §6.5.1) — pass it through to
    // `syncFromURL` unmodified via the `...parsed` spread below. A legacy
    // numeric `filters=` param (no `fv=2`) or an absent `f=` param produces
    // no `filters` key at all (see `parseURLParams`), i.e. "the parser
    // produced nothing" — in that case, mirror the `segments` reset below:
    // filters are only a supported URL key on "brand" and "market" screen
    // types; for "segment" the key is not part of the URL schema so we must
    // NOT overwrite it.
    const filtersSupported = !supportedURLKeys || supportedURLKeys.includes("filters");
    if (
      filtersSupported &&
      (screenType === "brand" || screenType === "market") &&
      parsed.filters === undefined
    ) {
      parsed.filters = [];
    }

    const segmentsSupported = !supportedURLKeys || supportedURLKeys.includes("segments");
    if (segmentsSupported && !searchParams.has("segments")) {
      parsed.segments = [];
    }

    // Build the filtered payload for syncFromURL.
    // When supportedURLKeys is defined, strip out any parsed key that is not
    // declared as a supported URL param for this screen.  `module` and
    // `screenType` are derived (not URL params) and are always included.
    // `ageRange` is synthetic — its gating is already handled above.
    // `humanMetrics` is parsed from the singular "humanMetric" URL key (see
    // parseURLParams), so it is gated against that URL key's presence in
    // supportedURLKeys rather than its own state name. `models` needs no such
    // mapping — its URL key and state name are both "models".
    const filteredParsed: Record<string, any> =
      !supportedURLKeys
        ? { ...parsed }
        : Object.fromEntries(
            Object.entries(parsed).filter(
              ([key]) =>
                // Always allow synthetic/derived keys that are not raw URL params
                (key === "humanMetrics"
                  ? supportedURLKeys.includes(HUMAN_METRIC)
                  : supportedURLKeys.includes(key)),
            ),
          );

    dispatch(syncFromURL({ ...filteredParsed, module, screenType } as any));

    // If we successfully converted legacy ageStart/ageEnd to a RANGE
    // FilterSelection, trigger the selectionListener so it navigates to the
    // new URL format (fv=2&f=<code>:r=<min>-<max>).
    if (legacyAgeRangeHandled && filteredParsed.filters !== undefined) {
      dispatch(
        setPendingSelection({
          filters: filteredParsed.filters as unknown as UserSelectionState["filters"],
        }),
      );
    }

    // ── Snapshot current screen selection → screenLevelSelection ────────────
    // Fired on every URL commit (including initial load and navigateTo calls
    // from selectionListener).  Saves the URL-derived brands/markets/segments/
    // secondaryBrands so that ModeDropdown / screen switches can restore them.
    // Only snapshot entity arrays that are declared as supported URL keys for
    // this screen to avoid overwriting valid state with empty arrays.
    const brandsSupported = !supportedURLKeys || supportedURLKeys.includes("brands");
    const marketsSupported = !supportedURLKeys || supportedURLKeys.includes("markets");
    const secondaryBrandsSupported = !supportedURLKeys || supportedURLKeys.includes("secondaryBrands");

    const contextKey = buildContextKey(
      screenType,
      (parsed.mode as string) ?? "POINT_IN_TIME",
      (parsed.visualizationType as string) ?? "",
      module,
    );
    dispatch(
      updateScreenLevelSelection({
        screenType: contextKey,
        value: {
          ...(brandsSupported && { brands: (filteredParsed.brands as number[]) ?? [] }),
          ...(marketsSupported && { markets: (filteredParsed.markets as number[]) ?? [] }),
          ...(segmentsSupported && { segments: (filteredParsed.segments as string[]) ?? [] }),
          ...(secondaryBrandsSupported && { secondaryBrands: (filteredParsed.secondaryBrands as number[]) ?? [] }),
          ...(filteredParsed[MODELS] && { models: filteredParsed[MODELS] as number[] }),
        },
      }),
    );
  }, [location]);

  // ── Retry: convert pending legacy age range once catalog loads ──────────
  // When the URL had legacy `ageStart`/`ageEnd` params but the filter catalog
  // wasn't loaded yet (ageFilterCode was undefined), we stored the age range
  // in pendingAgeRangeRef. Now that ageFilterCode is available, create the
  // RANGE FilterSelection and dispatch setPendingSelection so selectionListener
  // navigates to the new URL format.
  useEffect(() => {
    if (ageFilterCode && pendingAgeRangeRef.current) {
      const { start, end, existingFilters } = pendingAgeRangeRef.current;
      pendingAgeRangeRef.current = null;
      const ageRangeSelection: FilterSelection = {
        code: ageFilterCode,
        kind: "RANGE",
        range: { min: start, max: end },
      };
      const mergedFilters: FilterSelection[] = [
        ...existingFilters.filter((f) => f.code !== ageFilterCode),
        ageRangeSelection,
      ];
      dispatch(
        setPendingSelection({
          filters: mergedFilters as unknown as UserSelectionState["filters"],
        }),
      );
    }
  }, [ageFilterCode]);
};

export default useURLToStore;
